import { useLocalStorageState } from '@panwds/react-ui';
import { blueSteelScale, classNames, twMerge } from '@panwds/tailwindcss';
import { animated, useSpring } from '@react-spring/web';
import React, { useEffect, useMemo, useReducer, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Logo } from '../Logo/Logo.js';
import { InnerNavigationProvider } from '../NavigationContext/index.js';
import { Collapse } from './Collapse.js';
import { navActions, reducer } from './reducer.js';
export const navAutoCollapseWidth = 1024;
export function Navigation({ children, addClassName, logoClassName, clickToExpand, dataMetrics, dataTestId, logo, logoUrl, fullName, hasCollapseController, isTwoLines, onCollapseChange, navRef: _ref }) {
    const [storedState, setStoredState] = useLocalStorageState('navigationState', { collapsed: false });
    const [state, dispatch] = useReducer(reducer, {
        ...storedState,
        collapsed: storedState.collapsed || window.innerWidth < navAutoCollapseWidth
    });
    const intl = useIntl();
    const previousWidth = useRef(window.innerWidth);
    const navRef = useRef(null);
    const navClasses = classNames('twds-flex twds-flex-col twds-font-lato twds-flex-shrink-0 twds-h-screen twds-relative', 'twds-bg-blue-steel-900 dark:twds-bg-black');
    const initialWidth = useMemo(() => (state.collapsed ? '4rem' : '18rem'), []);
    const { width } = useSpring({
        from: { width: initialWidth },
        to: {
            width: state.collapsed ? '4rem' : '18rem'
        }
    });
    const { hoverTimeOut, ...rest } = state;
    useEffect(() => {
        setStoredState({ ...rest, openedNav: undefined });
    }, [state]);
    useEffect(() => {
        function navigationResizeHandler() {
            if (!state.collapsed &&
                previousWidth.current > navAutoCollapseWidth &&
                window.innerWidth <= navAutoCollapseWidth) {
                dispatch({ type: navActions.toggleCollapse });
            }
            previousWidth.current = window.innerWidth;
        }
        window.addEventListener('resize', navigationResizeHandler);
        onCollapseChange?.(state.collapsed);
        return () => window.removeEventListener('resize', navigationResizeHandler);
    }, [state.collapsed]);
    return (React.createElement(InnerNavigationProvider, { value: { ...state, dispatch, navRef } },
        React.createElement(animated.div, { "data-metrics": dataMetrics, "data-testid": dataTestId || 'navigation', style: { width }, className: navClasses, ref: _ref },
            React.createElement("nav", { ref: navRef, className: twMerge('twds-relative twds-flex twds-flex-col twds-flex-1 twds-box-border', state.collapsed ? 'twds-contents' : 'twds-overflow-hidden', addClassName), "aria-label": intl.formatMessage({
                    defaultMessage: 'Main Navigation',
                    id: 'Ekp0pa',
                    description: 'Label for navigation'
                }), style: { color: blueSteelScale[300] } },
                React.createElement(Logo, { logo: logo, fullName: fullName, isTwoLines: isTwoLines, logoUrl: logoUrl, addClassName: logoClassName }),
                React.createElement("div", { "data-testid": "navbar", onClick: expandNavBar, className: 'twds-box-border twds-flex twds-flex-col twds-flex-1 twds-justify-between twds-min-h-0 twds-mt-1' }, children)),
            hasCollapseController && (React.createElement(Collapse, { collapsed: state.collapsed, changeCollapse: () => dispatch({ type: navActions.toggleCollapse }) })))));
    function expandNavBar(e) {
        if (e.target === e.currentTarget) {
            state.collapsed && clickToExpand && dispatch({ type: navActions.toggleCollapse });
        }
    }
}
//# sourceMappingURL=Navigation.js.map